import Divider from 'components/divider/Divider';
import { FormCloseIcon } from 'assets/icons';
import { CustomButton, CustomInput, CustomModal } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import Loader from 'components/loader/Loader';
import { REGEX } from 'constants/common';
import { useValidateAddressMutation } from 'containers/customers/api';
import { ADDRESS_TYPE, initialAddressState } from 'containers/customers/constants';
import { CustomerAddress } from 'containers/customers/customers.model';
import { selectStates } from 'containers/customers/customersSlice';
import { AddressField } from 'containers/invoices/invoices.model';
import { useGetStatesListQuery } from 'containers/team-profile/api';
import { useAppSelector } from 'hooks';
import { FC, useEffect, useRef, useState } from 'react';
import { ParsedAddress, VoidFn, clevertapEvents } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { isEmptyObject } from 'utils/commonUtils';
import { JobsCleverTapEvents } from '../events';
import debounce from 'lodash/debounce';
import { createPlacesAutocompleteClients, googleAdressParser, whenGooglePlacesReady } from 'utils/maps';

interface AddressModalProps {
  className?: string;
  onClose: VoidFn;
  type: ADDRESS_TYPE;
  onAddAddress: (customer) => void;
  isEdit?: boolean;
  initialAddress?: CustomerAddress | AddressField;
  isLoading?: boolean;
  modalTitle?: string;
  primaryButtonText?: string;
}

enum AddressFields {
  street = 'street',
  apartment = 'apartment',
  city = 'city',
  state = 'state',
  zipCode = 'zipCode'
}

const errorsInitialState = {
  state: '',
  zipCode: '',
  city: '',
  street: '',
  apartment: ''
};

const AddressModal: FC<AddressModalProps> = ({
  onClose,
  type = ADDRESS_TYPE.SERVICE,
  onAddAddress,
  isEdit = false,
  initialAddress = initialAddressState,
  isLoading = false,
  modalTitle = null
}) => {
  const [addressString, setAddressString] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [fullAddress, setFullAddress] = useState<ParsedAddress>(initialAddressState);
  const [suggestedAddress, setSuggestedAddress] = useState<ParsedAddress>(initialAddressState);
  const [note, setNote] = useState('');
  const [saveAddressAs, setSaveAddressAs] = useState('');
  const [errors, setErrors] = useState<Record<AddressFields, string>>(errorsInitialState);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const showExpanded = fullAddress?.city || fullAddress?.state || fullAddress?.zipCode;

  const autoCompleteRef = useRef(null);
  const sessionToken = useRef(null);
  const placesService = useRef(null);
  const suggestionsCache = useRef({});
  const latestAddressQueryRef = useRef('');

  useGetStatesListQuery({ storeData: true });
  const [validateAddress] = useValidateAddressMutation();

  const statesList = useAppSelector(selectStates);

  useEffect(() => {
    const unsub = whenGooglePlacesReady(() => {
      const clients = createPlacesAutocompleteClients();
      if (!clients) return;
      autoCompleteRef.current = clients.autocomplete;
      placesService.current = clients.places;
      sessionToken.current = clients.sessionToken;
    });

    return () => {
      unsub();
      const input = document.getElementById('autocomplete-address-input');
      if (input && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(input);
      sessionToken.current = null;
    };
  }, []);

  useEffect(() => {
    const refreshPlacesSession = () => {
      const clients = createPlacesAutocompleteClients();
      if (!clients) return;
      autoCompleteRef.current = clients.autocomplete;
      placesService.current = clients.places;
      sessionToken.current = clients.sessionToken;
      suggestionsCache.current = {};
    };

    const handlePlacesSessionOnResume = () => {
      if (document.visibilityState !== 'visible') return;
      refreshPlacesSession();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) refreshPlacesSession();
    };

    document.addEventListener('visibilitychange', handlePlacesSessionOnResume);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handlePlacesSessionOnResume);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const debouncedPredictions = useRef(
    debounce((input, callback) => {
      if (!autoCompleteRef.current) {
        const clients = createPlacesAutocompleteClients();
        if (clients) {
          autoCompleteRef.current = clients.autocomplete;
          placesService.current = clients.places;
          sessionToken.current = clients.sessionToken;
        }
      }

      const options = {
        componentRestrictions: { country: 'us' },
        fields: ['address_components'],
        strictBounds: false,
        types: ['address']
      };

      if (suggestionsCache.current[input]?.length > 0) {
        if (input !== latestAddressQueryRef.current) return;
        callback(suggestionsCache.current[input]);
        return;
      }

      if (input && autoCompleteRef.current) {
        autoCompleteRef.current.getPlacePredictions(
          { input, sessionToken: sessionToken.current, ...options },
          (predictions, status) => {
            if (input !== latestAddressQueryRef.current) return;
            const hasResults =
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              Array.isArray(predictions) &&
              predictions.length > 0;
            if (hasResults) {
              suggestionsCache.current[input] = predictions;
              callback(predictions);
            } else {
              callback([]);
            }
          }
        );
      } else {
        if (input !== latestAddressQueryRef.current) return;
        callback([]);
      }
    }, 600)
  ).current;

  useEffect(() => {
    if (type === ADDRESS_TYPE.SERVICE) {
      logAnalyticEvent(JobsCleverTapEvents.webServiceAddressPageView);
    }
  }, []);

  useEffect(() => {
    if (isEdit) {
      const { street, addressName, addressNote, apartment, city, country, zipCode, state, latitude, longitude } =
        initialAddress;
      setFullAddress({ street, apartment, city, country, zipCode, state, latitude, longitude });
      setSuggestedAddress({ street, apartment, city, country, zipCode, state });
      setAddressString(street);
      setNote(addressNote);
      setSaveAddressAs(addressName);
    }
  }, [isEdit]);

  const addAddressAnalytics = (input, addressSuggestions) => {
    logAnalyticEvent(clevertapEvents.WebAddressPredictions, {
      'Address Predictions': JSON.stringify(addressSuggestions.map(suggestion => suggestion.description)),
      'Search Input': input
    });
  };

  const handleInputChange = value => {
    latestAddressQueryRef.current = value;
    setAddressString(value);

    setShowSuggestions(value.length >= 3);

    const cachedPrefix = Object.keys(suggestionsCache.current).find(
      key => value.startsWith(key) && suggestionsCache.current[key]?.length > 0
    );

    if (cachedPrefix) {
      const filteredSuggestions = suggestionsCache.current[cachedPrefix].filter(
        suggestion => suggestion.description.toLowerCase().includes(value.toLowerCase())
        // eslint-disable-next-line function-paren-newline
      );

      if (filteredSuggestions.length > 0) {
        setIsSuggestionsLoading(false);
        setSuggestions(filteredSuggestions);
        addAddressAnalytics(value, filteredSuggestions);
        return;
      }
    }

    if (value.length >= 3) {
      setIsSuggestionsLoading(true);
      debouncedPredictions(value, predictions => {
        setSuggestions(predictions);
        setIsSuggestionsLoading(false);
        addAddressAnalytics(value, predictions);
      });
      return;
    }

    setIsSuggestionsLoading(false);
    setSuggestions([]);
    if (!value) {
      setErrors(prevState => ({ ...prevState, street: 'Please enter a valid street name.' }));
    } else {
      setErrors(prevState => ({ ...prevState, street: '' }));
    }
  };

  const handleSuggestionClick = placeSuggestion => {
    const { place_id: placeId, description } = placeSuggestion;
    setAddressString(description);
    setIsSuggestionsLoading(false);

    placesService.current?.getDetails(
      {
        placeId,
        fields: ['address_components', 'geometry'],
        sessionToken: sessionToken.current
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const address: ParsedAddress = {
            ...googleAdressParser(place.address_components),
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          };

          setFullAddress(address);
          setSuggestedAddress(address);
          setShowSuggestions(false);
          setErrors(errorsInitialState);
          setSaveAddressAs(`${address.street}, ${address.city}`);
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      }
    );
  };

  const handleAddressChange = (key, value) => {
    setErrors(prevState => ({ ...prevState, [key]: '' }));
    if (key === AddressFields.state) {
      setErrors(prevState => ({ ...prevState, city: '' }));
    }
    if (key === AddressFields.zipCode) {
      if (REGEX.zipCodeOnChange.test(value) || value === '') {
        setFullAddress(prevState => ({ ...prevState, [key]: value }));
      }
    } else {
      setFullAddress(prevState => ({ ...prevState, [key]: value }));
    }
  };

  const checkIfValidState = addressState => {
    const isValidState = statesList.some(
      state =>
        state.name.toLowerCase() === addressState.toLowerCase() ||
        state.state_code.toLowerCase() === addressState.toLowerCase()
    );
    return isValidState;
  };

  const getStateCode = state => {
    const { state_code } = statesList.find(
      stateItem =>
        stateItem.name.toLowerCase() === state.trim().toLowerCase() ||
        stateItem.state_code.toLowerCase() === state.trim().toLowerCase()
    );
    return state_code;
  };

  const onAddAddressClick = async () => {
    let isValid = true;
    if (!REGEX.zipCode.test(fullAddress.zipCode)) {
      setErrors(prevState => ({ ...prevState, zipCode: 'Please enter a valid zip code.' }));
      isValid = false;
    }

    if (fullAddress.state !== suggestedAddress.state || fullAddress.city !== suggestedAddress.city) {
      if (!checkIfValidState(fullAddress.state) || !fullAddress.state) {
        setErrors(prevState => ({ ...prevState, state: 'Please enter a valid state.' }));
        isValid = false;
      }
      if (fullAddress.state && checkIfValidState(fullAddress.state)) {
        setIsSaveLoading(true);
        try {
          await validateAddress({
            street: fullAddress.street?.trim(),
            apartment: fullAddress.apartment?.trim(),
            city: fullAddress.city?.trim(),
            state: fullAddress.state?.trim(),
            stateCode: getStateCode(fullAddress.state),
            zipCode: fullAddress.zipCode?.trim(),
            country: fullAddress.country?.trim()
          }).unwrap();
          setIsSaveLoading(false);
        } catch (error) {
          setErrors(prevState => ({ ...prevState, city: 'Please enter a valid city.' }));
          isValid = false;
          setIsSaveLoading(false);
        }
      }
    }

    if (isValid) {
      const addressPayload = {
        type,
        street: fullAddress.street?.trim(),
        city: fullAddress.city?.trim(),
        state: fullAddress.state?.trim(),
        apartment: fullAddress.apartment?.trim(),
        zipCode: fullAddress.zipCode?.trim(),
        country: fullAddress.country?.trim(),
        addressNote: note?.trim(),
        addressName: saveAddressAs,
        ...(isEdit && { id: initialAddress.id }),
        latitude: fullAddress?.latitude ?? 0,
        longitude: fullAddress?.longitude ?? 0
      };
      onAddAddress(addressPayload);
    }
  };

  const shouldEnableAddButton = () => {
    if (isEdit) {
      if (!isEmptyObject(fullAddress) && !isEmptyObject(initialAddress)) {
        return (
          fullAddress.street !== initialAddress.street ||
          fullAddress.apartment !== initialAddress.apartment ||
          fullAddress.city !== initialAddress.city ||
          fullAddress.state !== initialAddress.state ||
          fullAddress.zipCode !== initialAddress.zipCode ||
          note !== initialAddress.addressNote ||
          saveAddressAs !== initialAddress.addressName
        );
      }
    } else {
      if (!isEmptyObject(fullAddress)) {
        return (
          fullAddress.street && fullAddress.city && fullAddress.state && fullAddress.zipCode && saveAddressAs.trim()
        );
      }
    }
    return false;
  };

  const getModalTitle = () => {
    if (modalTitle) {
      return modalTitle;
    }
    return `${isEdit ? 'Edit' : 'Add'} ${type === ADDRESS_TYPE.SERVICE ? 'service' : 'billing'} address`;
  };

  return (
    <CustomModal
      open={true}
      width="w-[90%] md:w-[607px]"
      height="h-[90%] md:max-h-[712px]"
      onOutsideClick={onClose}
      className="customNormalScroll overflow-auto">
      <div className="flex h-full flex-col overflow-auto p-4 md:h-[712px] md:px-8 md:py-6">
        <div className="flex items-center justify-between pb-6">
          <div className="text-xl font-bold text-heading">{getModalTitle()}</div>
          <FormCloseIcon width={26} height={26} className="cursor-pointer" onClick={onClose} />
        </div>
        <div className="grow space-y-4">
          <div className="relative">
            <div className="mb-2.5 text-sbase font-bold text-primaryText">
              Street<span className="text-error">*</span>
            </div>
            <div className={errors.street ? 'pb-2' : ''}>
              <CustomInput
                value={addressString}
                onChange={handleInputChange}
                id="autocomplete-primary-address"
                placeholder="Enter street address"
              />
              {errors.street && <div className="absolute mt-1 text-xs text-error">{errors.street}</div>}
            </div>
            {showSuggestions && (
              <div className="absolute z-10 mt-0.5 w-full rounded border border-borderGray bg-primary shadow-md">
                {suggestions?.map(suggestion => (
                  <div
                    className="cursor-pointer p-3 hover:bg-slate-100"
                    key={suggestion.place_id}
                    onClick={() => handleSuggestionClick(suggestion)}>
                    {suggestion.description}
                  </div>
                ))}
                {!isSuggestionsLoading && suggestions?.length === 0 && (
                  <div className="p-3 text-center text-sm text-primaryText">No results found</div>
                )}
                {isSuggestionsLoading && (
                  <div className="p-2">
                    <Loader lottieClassName="h-8 w-8" />
                  </div>
                )}
              </div>
            )}
          </div>
          {showExpanded && (
            <>
              <div className="flex items-center gap-4">
                <div className="w-1/2">
                  <div className="mb-2.5 text-sbase font-semibold text-primaryText">Apt/Suite/Unit</div>
                  <div>
                    <CustomInput
                      id="Apt/Suite/Unit"
                      value={fullAddress.apartment}
                      onChange={value => handleAddressChange(AddressFields.apartment, value)}
                      placeholder="Apt/Suite/Unit"
                    />
                  </div>
                </div>
                <div className="w-1/2">
                  <div className="mb-2.5 text-sbase font-semibold text-primaryText">
                    City<span className="text-error">*</span>
                  </div>
                  <div>
                    <CustomInput
                      id="City"
                      value={fullAddress.city}
                      onChange={value => handleAddressChange(AddressFields.city, value)}
                      placeholder="Enter city"
                    />
                    {errors.city && <div className="absolute mt-1 text-xs text-error">{errors.city}</div>}
                  </div>
                </div>
              </div>

              <div className={`flex items-center gap-4 ${errors.state || errors.zipCode ? 'pb-3' : ''}`}>
                <div className="w-1/2">
                  <div className="mb-2.5 text-sbase font-semibold text-primaryText">
                    State<span className="text-error">*</span>
                  </div>
                  <div>
                    <CustomInput
                      id="State"
                      value={fullAddress.state}
                      onChange={value => handleAddressChange(AddressFields.state, value)}
                      placeholder="Enter state"
                    />
                    {errors.state && <div className="absolute mt-1 text-xs text-error">{errors.state}</div>}
                  </div>
                </div>
                <div className="w-1/2">
                  <div className="mb-2.5 text-sbase font-semibold text-primaryText">
                    Zip code<span className="text-error">*</span>
                  </div>
                  <div>
                    <CustomInput
                      id="Zip-code"
                      value={fullAddress.zipCode}
                      onChange={value => handleAddressChange(AddressFields.zipCode, value)}
                      placeholder="Enter zip code"
                    />
                    {errors.zipCode && <div className="absolute mt-1 text-xs text-error">{errors.zipCode}</div>}
                  </div>
                </div>
              </div>

              <Divider variant="fullWidth" />

              <div>
                <div className="text-sbase font-bold text-primaryText">More details</div>
                <div className="text-sbase text-primaryText">Store optional details for your own records.</div>
              </div>

              <div>
                <div className="mb-2.5 text-sbase font-bold text-primaryText">Address note</div>
                <div>
                  <CustomInput value={note} onChange={setNote} id="address-note" placeholder="Enter address note" />
                </div>
              </div>

              <div>
                <div className="mb-2.5 text-sbase font-bold text-primaryText">
                  Save address as<span className="text-error">*</span>
                </div>
                <div>
                  <CustomInput
                    value={saveAddressAs}
                    onChange={setSaveAddressAs}
                    id="save-address-as"
                    placeholder="Save address as"
                  />
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mt-6 flex items-center justify-end gap-4">
          <CustomButton
            type={ButtonType.SECONDARY}
            id="back-address-button"
            className="h-10 w-[100px]"
            onClick={onClose}>
            Back
          </CustomButton>
          <CustomButton
            type={ButtonType.PRIMARY}
            id="add-address-button"
            className="h-10 w-[100px]"
            onClick={onAddAddressClick}
            disabled={!shouldEnableAddButton()}
            isLoading={isLoading || isSaveLoading}>
            {isEdit ? 'Update' : 'Add'}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddressModal;
